import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, User, Phone, Loader2, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Patients() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toDelete, setToDelete] = useState(null);
    const [form, setForm] = useState({
        name: "",
        phone: "",
        id_no: "",
        notes: "",
    });

    async function load() {
        const { data } = await api.get("/patients");
        setItems(data);
    }
    useEffect(() => {
        load();
    }, []);

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/patients", form);
            toast.success("Hasta eklendi");
            setOpen(false);
            setForm({ name: "", phone: "", id_no: "", notes: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    const filtered = items.filter(
        (p) =>
            p.name.toLowerCase().includes(q.toLowerCase()) ||
            (p.phone || "").includes(q),
    );

    async function confirmDelete() {
        if (!toDelete) return;
        try {
            await api.delete(`/patients/${toDelete.id}`);
            toast.success("Hasta silindi");
            setToDelete(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    return (
        <div data-testid="patients-page">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">
                        Hastalar
                    </h1>
                    <p className="text-slate-600 mt-1">
                        Klinikteki tüm hasta kayıtları
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button
                            className="dk-btn-primary rounded-full"
                            data-testid="add-patient-btn"
                        >
                            <Plus className="w-4 h-4 mr-1" /> Yeni Hasta
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Yeni Hasta Kaydı</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-3">
                            <div>
                                <Label>Ad Soyad</Label>
                                <Input
                                    required
                                    value={form.name}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            name: e.target.value,
                                        })
                                    }
                                    data-testid="patient-name-input"
                                />
                            </div>
                            <div>
                                <Label>Telefon</Label>
                                <Input
                                    value={form.phone}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            phone: e.target.value,
                                        })
                                    }
                                    data-testid="patient-phone-input"
                                />
                            </div>
                            <div>
                                <Label>TC / Pasaport No</Label>
                                <Input
                                    value={form.id_no}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            id_no: e.target.value,
                                        })
                                    }
                                    data-testid="patient-idno-input"
                                />
                            </div>
                            <div>
                                <Label>Notlar</Label>
                                <Textarea
                                    value={form.notes}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            notes: e.target.value,
                                        })
                                    }
                                    data-testid="patient-notes-input"
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={saving}
                                className="dk-btn-primary w-full"
                                data-testid="patient-submit-btn"
                            >
                                {saving ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    "Kaydet"
                                )}
                            </Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="mb-4 relative max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                    placeholder="Hasta ara..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="pl-9 bg-white"
                    data-testid="patient-search-input"
                />
            </div>

            <Card>
                <CardContent className="p-0">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                                <th className="px-6 py-3">Ad Soyad</th>
                                <th className="px-6 py-3">Telefon</th>
                                <th className="px-6 py-3">Kayıt Tarihi</th>
                                <th className="px-6 py-3 text-right">
                                    İşlem
                                </th>
                            </tr>
                        </thead>
                        <tbody data-testid="patients-table-body">
                            {filtered.map((p) => (
                                <tr
                                    key={p.id}
                                    className="border-b hover:bg-slate-50"
                                >
                                    <td className="px-6 py-4 font-medium flex items-center gap-2">
                                        <User className="w-4 h-4 text-teal-600" />
                                        {p.name}
                                    </td>
                                    <td className="px-6 py-4 text-slate-700 flex items-center gap-1">
                                        <Phone className="w-3.5 h-3.5 text-slate-400" />
                                        {p.phone || "-"}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-slate-500">
                                        {p.created_at?.slice(0, 10)}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            <Link
                                                to={`/dashboard/patients/${p.id}`}
                                            >
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    data-testid={`patient-detail-${p.id}`}
                                                >
                                                    Aç
                                                </Button>
                                            </Link>
                                            {user?.role === "patron" && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200"
                                                    onClick={() =>
                                                        setToDelete(p)
                                                    }
                                                    data-testid={`patient-delete-${p.id}`}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="text-center text-slate-500 py-12"
                                    >
                                        Hasta bulunamadı.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </CardContent>
            </Card>

            <AlertDialog
                open={!!toDelete}
                onOpenChange={(o) => !o && setToDelete(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Hasta silinsin mi?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <strong>{toDelete?.name}</strong> hastası, tüm
                            tedavi anlaşmaları ve ödeme kayıtları kalıcı
                            olarak silinecek. Bu işlem geri alınamaz.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="patient-delete-cancel">
                            İptal
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            className="bg-rose-600 hover:bg-rose-700"
                            data-testid="patient-delete-confirm"
                        >
                            Evet, sil
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
